import React from 'react';

import { List, ListItem, ListItemText, Paper, WithStyles, withStyles } from '@material-ui/core';
import { PaperProps } from '@material-ui/core/Paper';
import { createStyles } from '@material-ui/core/styles';
import { ArrowRight } from '@material-ui/icons';
import { get } from 'lodash';

import { Link, Typography } from 'components/v2';
import { useNextyContext } from 'containers/App/hooks';
import usePermissions from 'hooks/usePermissions';
import useResizeObserver from 'hooks/useResizeObserver';
import { Tab } from 'screens/MissionControl/constants/settingsItems';

export const useStyles = () =>
  createStyles({
    noUnderline: {
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'none',
      },
      '&:visited': {
        textDecoration: 'none',
      },
      '&:link': {
        textDecoration: 'none',
      },
      '&:active': {
        textDecoration: 'none',
      },
    },
    list: {
      padding: 0,
    },
    listItem: {
      paddingRight: 8,
      height: 48,
    },
    listItemText: {
      padding: 0,
    },
    listItemTextTypography: {
      display: 'flex',
      justifyContent: 'space-between',
      transition: 'opacity 0.2s ease-out',
      alignItems: 'center',
    },
    arrow: {
      marginLeft: 10,
    },
  });

interface tabsWithNestedMenusMapProps {
  [key: string]: Tab[];
}
interface CommonMenuPopoverProps {
  paperProps?: PaperProps;
  anchorElements: HTMLElement[];
  setAnchorElements: React.Dispatch<React.SetStateAction<HTMLElement[]>>;
}

interface MenuPopoverProps extends WithStyles<typeof useStyles>, CommonMenuPopoverProps {
  tabsWithNestedMenusMap: tabsWithNestedMenusMapProps;
  level: number;
}

const MenuPopover: React.FC<MenuPopoverProps> = ({
  classes,
  anchorElements,
  setAnchorElements,
  paperProps = {},
  tabsWithNestedMenusMap,
  level,
}) => {
  const { hasAccessToProduct } = usePermissions();
  const { nextyStorageValue } = useNextyContext();
  const anchorElement = anchorElements[level];
  const tabId = get(anchorElement, 'id');
  const tabsToDisplayInMenu = get(tabsWithNestedMenusMap, tabId) || [];

  // useResizeObserver to avoid the menu to not follow the drawer on resize.
  const [{ width, left, top: topObserver, height: heightObserver }] = useResizeObserver(anchorElement);

  if (!anchorElement) {
    return null;
  }

  const top = nextyStorageValue ? topObserver - 5 : topObserver;
  const calcHeight = top - 1.25 * heightObserver;
  const heightTransform = nextyStorageValue ? calcHeight + 65 : calcHeight;

  return (
    <Paper
      {...paperProps}
      style={{
        position: 'absolute',
        overflow: 'auto',
        ...paperProps.style,
        transform: `translate(${left + width}px, ${heightTransform}px)`,
        zIndex: 150,
        maxHeight: 400,
      }}
    >
      <List className={classes.list}>
        {tabsToDisplayInMenu.map(({ label, key, action, path, subMenu, provisioning }) => {
          if (provisioning.length && !hasAccessToProduct(provisioning)) return null;
          if (subMenu) {
            return (
              <ListItem
                key={key}
                id={key}
                button
                onClick={action}
                className={classes.listItem}
                onMouseEnter={(event) => {
                  // NOTE: [line below] if event.currentTarget isn't "cached" (set to a const) it will be nullified
                  // by react when it hyjacks and pools native brower events (which are wrapped with SyntheticEvents).
                  // refer to: https://duncanleung.com/fixing-react-warning-synthetic-events-in-setstate/
                  const currentTarget = event.currentTarget;
                  setAnchorElements((anchors) => [...anchors.slice(0, level + 1), currentTarget]);
                }}
              >
                <ListItemText
                  className={classes.listItemText}
                  primary={
                    <Typography className={classes.listItemTextTypography} variant="h3W500F15">
                      {label}
                      <ArrowRight className={classes.arrow} color="primary" />
                    </Typography>
                  }
                />
              </ListItem>
            );
          }
          if (path) {
            return (
              <Link
                key={key}
                id={key}
                className={classes.noUnderline}
                to={key === 'form-builder' ? '/v2/form-builder' : path}
              >
                <ListItem
                  button
                  className={classes.listItem}
                  onClick={() => {
                    action();
                    setAnchorElements([]);
                  }}
                  onMouseEnter={() => {
                    setAnchorElements((anchors) => anchors.slice(0, level + 1));
                  }}
                >
                  <ListItemText primary={<Typography variant="h3W500F15">{label}</Typography>} />
                </ListItem>
              </Link>
            );
          }
          return (
            <ListItem
              key={key}
              id={key}
              button
              className={classes.listItem}
              onClick={() => {
                action();
                setAnchorElements([]);
              }}
              onMouseEnter={() => {
                setAnchorElements((anchors) => anchors.slice(0, level + 1));
              }}
            >
              <ListItemText primary={<Typography variant="h3W500F15">{label}</Typography>} />
            </ListItem>
          );
        })}
      </List>
    </Paper>
  );
};

interface NestedMenuPopoverProps extends WithStyles<typeof useStyles>, CommonMenuPopoverProps {
  nestedSettingsTabs: Tab[];
}

const NestedMenuPopover: React.FC<NestedMenuPopoverProps> = ({ anchorElements, nestedSettingsTabs, ...props }) => {
  const tabsWithNestedMenusMap: tabsWithNestedMenusMapProps = {};
  const nestedSubMenus: number[] = [0];
  const buildSubMenuMap = (menuList: Tab[], subMenus: number = -1) => {
    if (nestedSubMenus.length - 1 < subMenus) nestedSubMenus.push(subMenus);
    menuList.forEach((tab: Tab) => {
      if (tab.subMenu) {
        tabsWithNestedMenusMap[tab.key] = tab.subMenu;
        buildSubMenuMap(tab.subMenu, subMenus + 1);
      }
    });
  };

  buildSubMenuMap(nestedSettingsTabs);

  return (
    <>
      {nestedSubMenus.map((level) => (
        <MenuPopover
          anchorElements={anchorElements}
          tabsWithNestedMenusMap={tabsWithNestedMenusMap}
          key={level}
          level={level}
          {...props}
        />
      ))}
    </>
  );
};

export default withStyles(useStyles)(NestedMenuPopover);
